import React, { useState, useMemo, useEffect } from "react";
import {
  Activity,
  Shield,
  AlertTriangle,
  Briefcase,
  Globe,
  TrendingUp,
  TrendingDown,
  Minus,
} from "lucide-react";
import Modal from "../common/Modal";
import { RiskInsightsSkeleton } from "../common/InsightsSkeleton";
import Toast, { type ToastMessage } from "../common/Toast";
import RiskVisualization from "./RiskVisualization";
import riskRegisterService, { RiskRegister } from "../../services/riskRegisterService";
import riskScoresService, { RiskScore } from "../../services/riskScoresService";
import NoDataFound from "../common/NoDataFound";
import { usePermission } from "../../contexts/PermissionContext";

// =============================================================================
// Types
// =============================================================================

type RiskRow = {
  id: number;            // DB id — required to open canvas
  code: string;
  name: string;
  category: string;
  likelihood: number;    // 1..5
  severity: number;      // 1..5
  inherent: number;      // L * S (1..25)
  residual: number;      // computed by backend (0..25)
  residualLikelihood: number; // synthetic: residual / severity, rounded, clamped
  hasScore: boolean;
};

// =============================================================================
// Helpers
// =============================================================================

const clamp = (n: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, n));

const getRiskColor = (score: number) => {
  if (score >= 20) return "status-badge xs danger";
  if (score >= 15) return "status-badge xs warning";
  if (score >= 8) return "status-badge xs warning";
  if (score >= 4) return "status-badge xs success";
  return "status-badge xs success";
};

// =============================================================================
// Sub-components
// =============================================================================

// ── Skeleton card shells — caller owns layout, Skeleton owns shimmer only ─────


const StatCard = ({ title, value, subtext, icon: Icon, trendIcon: TrendIcon, bg, fg, iconColor }: any) => (
  <div className="relative bg-white rounded-2xl flex items-center gap-4 px-5 py-5 cursor-default overflow-hidden"
    style={{
      border: '1px solid #e8ecf4',
      boxShadow: '0 4px 20px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)',
      transition: 'box-shadow 0.2s ease, transform 0.2s ease',
    }}
    onMouseEnter={e => {
      (e.currentTarget as HTMLDivElement).style.boxShadow = '0 8px 28px rgba(0,0,0,0.11), 0 2px 6px rgba(0,0,0,0.06)';
      (e.currentTarget as HTMLDivElement).style.transform = 'translateY(-1px)';
    }}
    onMouseLeave={e => {
      (e.currentTarget as HTMLDivElement).style.boxShadow = '0 4px 20px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)';
      (e.currentTarget as HTMLDivElement).style.transform = 'translateY(0)';
    }}>
    {iconColor && (
      <div className="absolute bottom-0 right-0 w-24 h-24 rounded-full pointer-events-none"
        style={{ background: `radial-gradient(circle, ${iconColor}0d 0%, transparent 70%)`, transform: 'translate(30%, 30%)' }} />
    )}
    <div className={`shrink-0 p-3 rounded-xl ${bg}`}
      style={iconColor ? { boxShadow: `0 2px 10px ${iconColor}28` } : undefined}>
      {Icon ? <Icon size={20} className={fg} /> : TrendIcon && <TrendIcon size={20} className={fg} />}
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 truncate mb-1">{title}</p>
      <span className="text-[2rem] font-extrabold text-slate-800 leading-none tabular-nums">{value}</span>
      {subtext && <p className="text-[10px] text-slate-400 mt-1 truncate">{subtext}</p>}
    </div>
  </div>
);

const LEGEND = [
  { label: 'V. Low', cls: 'bg-green-700' },
  { label: 'Low',    cls: 'bg-lime-500' },
  { label: 'Mod',    cls: 'bg-yellow-400' },
  { label: 'High',   cls: 'bg-orange-500' },
  { label: 'Extreme',cls: 'bg-red-600' },
];

const RiskMatrix = ({
  title,
  description,
  icon: Icon,
  risks,
  getCell,
  onCellClick,
  showLegend,
}: any) => {
  const matrix = Array(5).fill(null).map(() => Array(5).fill(0));
  risks.forEach((r: RiskRow) => {
    const l = getCell(r);
    const s = r.severity;
    if (!l || !s) return;
    const row = 5 - l;
    const col = s - 1;
    if (row >= 0 && row < 5 && col >= 0 && col < 5) matrix[row][col]++;
  });

  const getCellColor = (rowIdx: number, colIdx: number) => {
    const l = 5 - rowIdx;
    const s = colIdx + 1;
    const score = l * s;
    // Standard 5x5 RAG heatmap: solid red / orange / yellow / light green / dark green.
    if (score >= 20) return "bg-red-600 hover:bg-red-700 border-red-700";
    if (score >= 15) return "bg-orange-500 hover:bg-orange-600 border-orange-600";
    if (score >= 8) return "bg-yellow-400 hover:bg-yellow-500 border-yellow-500";
    if (score >= 4) return "bg-lime-500 hover:bg-lime-600 border-lime-600";
    return "bg-green-700 hover:bg-green-800 border-green-800";
  };

  const getCellTextColor = (rowIdx: number, colIdx: number) => {
    const l = 5 - rowIdx;
    const s = colIdx + 1;
    const score = l * s;
    if (score >= 4 && score < 15) return "text-slate-900"; // dark text on yellow / light green
    return "text-white";
  };

  return (
    <div className="bg-white p-3 rounded-2xl"
      style={{ border: '1px solid #e8ecf4', boxShadow: '0 4px 20px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)' }}>
      <div className="flex items-center gap-2 mb-2">
        <div className="p-1.5 bg-slate-100 rounded-lg text-slate-600 shrink-0">
          <Icon size={16} />
        </div>
        <div>
          <h3 className="text-sm font-semibold text-slate-800">{title}</h3>
          <p className="text-xs text-slate-500">{description}</p>
        </div>
      </div>

      <div className="mt-3 flex">
        <div className="w-8 flex items-center justify-center">
          <span className="text-xs font-bold text-slate-400 -rotate-90 whitespace-nowrap tracking-widest uppercase">
            Likelihood
          </span>
        </div>

        <div className="flex-1">
          <div className="grid grid-cols-5 gap-1 aspect-square">
            {matrix.map((row, rowIdx) =>
              row.map((count, colIdx) => (
                <div
                  key={`${rowIdx}-${colIdx}`}
                  data-testid={`BTN_risk_overview_matrix_cell_${5 - rowIdx}_${colIdx + 1}`}
                  onClick={() => {
                    const l = 5 - rowIdx;
                    const s = colIdx + 1;
                    const cellRisks = risks.filter(
                      (r: RiskRow) => getCell(r) === l && r.severity === s,
                    );
                    onCellClick(
                      cellRisks,
                      { value: l, label: `L${l}` },
                      { value: s, label: `S${s}` },
                    );
                  }}
                  className={`relative border rounded-lg flex items-center justify-center cursor-pointer transition-all duration-200 ${getCellColor(rowIdx, colIdx)}`}
                >
                  {count > 0 && (
                    <span className={`text-sm font-bold ${getCellTextColor(rowIdx, colIdx)}`}>
                      {count}
                    </span>
                  )}
                </div>
              )),
            )}
          </div>
          <div className="h-8 flex items-center justify-center mt-2">
            <span className="text-xs font-bold text-slate-400 tracking-widest uppercase">
              Severity
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};

const DIST_FALLBACK_COLORS = ['#6366f1','#3b82f6','#06b6d4','#10b981','#f59e0b','#8b5cf6','#f43f5e','#94a3b8'];

const DistributionWidget = ({ title, data, icon: Icon, total, accentColor = '#6366f1', className = '' }: any) => {
  const maxCount = Math.max(...data.map((d: any) => d.count), 1);
  return (
    <div className={`rounded-2xl overflow-hidden flex flex-col ${className}`}
      style={{
        background: '#ffffff',
        border: '1px solid #e8ecf4',
        boxShadow: '0 4px 20px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)',
      }}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 bg-white">
        <div className="flex items-center gap-2">
          <div className="p-1.5 rounded-lg" style={{ background: `${accentColor}12` }}>
            <Icon size={12} style={{ color: accentColor }} />
          </div>
          <span className="text-[11px] font-bold uppercase tracking-wider text-slate-600">{title}</span>
        </div>
        {total > 0 && (
          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full tabular-nums"
            style={{ background: `${accentColor}12`, color: accentColor }}>
            {total}
          </span>
        )}
      </div>
      {data.length === 0 ? (
        <p className="text-xs text-slate-400 text-center py-4 italic">No data.</p>
      ) : (
        <ul className="divide-y divide-slate-50 flex-1">
          {data.map((item: any, idx: number) => {
            const color = item.color || DIST_FALLBACK_COLORS[idx % DIST_FALLBACK_COLORS.length];
            const pct = total > 0 ? Math.round((item.count / total) * 100) : 0;
            const barPct = item.count > 0 ? Math.round((item.count / maxCount) * 100) : 0;
            return (
              <li key={idx}
                className="grid items-center gap-3 px-4 py-2.5 transition-colors duration-100"
                style={{ gridTemplateColumns: '1fr 80px 3.5rem' }}
                onMouseEnter={e => (e.currentTarget.style.background = '#f8fafc')}
                onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}>
                <span className="text-[11px] font-semibold text-slate-600 uppercase tracking-wide truncate">{item.label}</span>
                <div className="h-1.5 rounded-full overflow-hidden"
                  style={{ background: 'linear-gradient(to bottom, #f1f5f9, #e2e8f0)' }}>
                  <div className="h-1.5 rounded-full transition-all duration-500"
                    style={{
                      width: barPct > 0 ? `${barPct}%` : '0%',
                      background: `linear-gradient(90deg, ${color}bb, ${color})`,
                      boxShadow: barPct > 10 ? `0 1px 4px ${color}55` : 'none',
                    }} />
                </div>
                <div className="flex items-center justify-end gap-1">
                  <span className="text-[12px] font-extrabold tabular-nums"
                    style={{ color: item.count > 0 ? color : '#94a3b8' }}>
                    {item.count}
                  </span>
                  <span className="text-[10px] text-slate-400">({pct}%)</span>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

const RiskDetailModal = ({ isOpen, onClose, data, context, onVisualize, metric }: any) => (
  <Modal isOpen={isOpen} onClose={onClose} size="lg">
    <Modal.Header>
      <span>
        {metric === "residual" ? "Residual" : "Inherent"} Risk Breakdown
        <span className="block text-sm font-normal text-slate-500 mt-0.5">
          Likelihood: <span className="font-bold text-slate-700">{context.likelihood}</span>{" "}
          • Severity: <span className="font-bold text-slate-700">{context.severity}</span>
        </span>
      </span>
    </Modal.Header>
    <Modal.Body>
      {data.length === 0 ? (
        <div className="text-sm text-slate-500 italic py-4">No risks in this cell.</div>
      ) : (
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="text-xs font-bold text-slate-400 uppercase tracking-wider border-b border-slate-200">
              <th className="pb-3 pl-2">Code</th>
              <th className="pb-3">Risk Name</th>
              <th className="pb-3">Category</th>
              <th className="pb-3 text-center">Inherent</th>
              <th className="pb-3 text-center">Residual</th>
              <th className="pb-3 text-center">Open</th>
            </tr>
          </thead>
          <tbody className="text-sm">
            {data.map((risk: RiskRow) => (
              <tr
                key={risk.id}
                data-testid={`BTN_risk_overview_detail_row_${risk.id}_open`}
                className="border-b border-slate-50 hover:bg-slate-50 transition-colors cursor-pointer"
                onClick={() => onVisualize(risk)}
              >
                <td className="py-3 pl-2 text-slate-500 text-xs">{risk.code}</td>
                <td className="py-3 font-medium text-slate-800">{risk.name}</td>
                <td className="py-3 text-sm text-slate-600">{risk.category || "—"}</td>
                <td className="py-3 text-center">
                  <span className={getRiskColor(risk.inherent)}>
                    {risk.inherent}
                  </span>
                </td>
                <td className="py-3 text-center">
                  <span className={getRiskColor(risk.residual)}>
                    {risk.residual}
                  </span>
                </td>
                <td className="py-3 text-center">
                  <button
                    onClick={(e) => { e.stopPropagation(); onVisualize(risk); }}
                    className="p-1.5 hover:bg-indigo-50 text-indigo-600 rounded-lg transition-colors"
                    title="Open Risk Canvas"
                    data-testid={`BTN_risk_overview_canvas_${risk.id}`}
                  >
                    <Activity size={16} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Modal.Body>
  </Modal>
);

// =============================================================================
// Main
// =============================================================================

const RiskOverview: React.FC = () => {
  const { allowed: canView, loading: permLoading } = usePermission('RSK_RISK_REGISTER', 'view');
  const [rows, setRows] = useState<RiskRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [toast, setToast] = useState<ToastMessage | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const [risks, latest] = await Promise.all([
          riskRegisterService.getAll({ status: 'ALL' }),
          riskScoresService.latestForEntity().catch(() => [] as RiskScore[]),
        ]);
        const scoreByRisk = new Map<number, RiskScore>();
        latest.forEach(s => { if (s.is_active === 1) scoreByRisk.set(s.risk_id, s); });

        const merged: RiskRow[] = risks
          .filter((r: RiskRegister) => r.is_active === 1)
          .map((r: RiskRegister) => {
            const s = scoreByRisk.get(r.id);
            const likelihood = s?.likelihood ?? 0;
            const severity = s?.severity ?? 0;
            const inherent = s?.inherent_risk ?? (likelihood * severity);
            const residual = s?.residual_risk ?? inherent;
            const residualLikelihood = severity > 0
              ? clamp(Math.round(residual / severity), 1, 5)
              : 0;
            return {
              id: r.id,
              code: r.risk_code,
              name: r.risk_name,
              category: r.category_name || "",
              likelihood,
              severity,
              inherent,
              residual,
              residualLikelihood,
              hasScore: !!s,
            };
          });
        if (!cancelled) setRows(merged);
      } catch (e: any) {
        if (!cancelled) setToast({ type: 'error', message: e?.message || "Failed to load risks" });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // A risk is "scored" only when its latest assessment carries both a
  // likelihood and a severity. A bare score record with empty L/S is NOT
  // scored — counting it inflated the heatmaps/severity mismatch. This is the
  // SAME definition the dashboard Risk Summary endpoint uses.
  const scoredRows = useMemo(
    () => rows.filter(r => r.likelihood > 0 && r.severity > 0),
    [rows],
  );

  // Insights from residual scores — five bands matching the legend & heatmap
  // colours. residual already falls back to inherent (L x S) when the backend
  // has no residual yet, so every scored row lands in exactly one band.
  const insights = useMemo(() => {
    const extreme = scoredRows.filter(r => r.residual >= 20).length;
    const high = scoredRows.filter(r => r.residual >= 15 && r.residual < 20).length;
    const moderate = scoredRows.filter(r => r.residual >= 8 && r.residual < 15).length;
    const low = scoredRows.filter(r => r.residual >= 4 && r.residual < 8).length;
    const vlow = scoredRows.filter(r => r.residual > 0 && r.residual < 4).length;

    const catCounts = new Map<string, number>();
    scoredRows.forEach(r => {
      const key = r.category || "Uncategorised";
      catCounts.set(key, (catCounts.get(key) || 0) + 1);
    });
    const categoryCounts = Array.from(catCounts.entries())
      .map(([label, count]) => ({ label, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 8);

    // High-level 3-band rollup — must equal the dashboard Risk Summary widget.
    const highBand = extreme + high;   // residual >= 15
    const mediumBand = moderate;       // residual 8-14
    const lowBand = low + vlow;        // residual < 8

    // 5-band residual distribution (legend order, with band colours).
    const residualBands = [
      { label: "Extreme (20+)", count: extreme, color: "#dc2626" },
      { label: "High (15–19)", count: high, color: "#f97316" },
      { label: "Moderate (8–14)", count: moderate, color: "#facc15" },
      { label: "Low (4–7)", count: low, color: "#84cc16" },
      { label: "V. Low (<4)", count: vlow, color: "#15803d" },
    ];

    return { extreme, high, moderate, low, vlow, highBand, mediumBand, lowBand, categoryCounts, residualBands };
  }, [scoredRows]);

  const [modalState, setModalState] = useState({
    isOpen: false,
    risks: [] as RiskRow[],
    context: { likelihood: 0, severity: 0 },
    metric: "inherent" as "inherent" | "residual",
  });

  const [visModalState, setVisModalState] = useState({
    isOpen: false,
    risk: null as any,
  });

  const handleCellClick = (metric: "inherent" | "residual") =>
    (risks: RiskRow[], rowConfig: any, colConfig: any) => {
      setModalState({
        isOpen: true,
        risks,
        context: { likelihood: rowConfig.value, severity: colConfig.value },
        metric,
      });
    };

  const handleVisualize = (risk: RiskRow) => {
    setModalState(prev => ({ ...prev, isOpen: false }));
    setVisModalState({
      isOpen: true,
      risk: {
        id: risk.id,
        risk_code: risk.code,
        risk_name: risk.name,
        title: risk.name,
        description: `Risk canvas for ${risk.name}.`,
        status: "Active",
        inherentScore: risk.inherent,
        residualScore: risk.residual,
      },
    });
  };

  // ── Not-authorised gate ─────────────────────────────────────────────────
  if (!permLoading && !canView) {
    return (
      <NoDataFound title="Not authorised" message="You do not have access to 'RISK_INSIGHTS'." />
    );
  }

  return (
    <div className="netbody-card flex flex-col h-full min-h-[500px]">
      <div className="netbody-content">
        <div className="scroll-container-nofilters flex-1 overflow-hidden">

          {loading && <RiskInsightsSkeleton />}

          {!loading && (
            <div className="py-4 px-2 space-y-4">

              {/* Stat cards — top row */}
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
                <StatCard
                  title="Total Risks"
                  value={rows.length}
                  subtext={`${scoredRows.length} scored, ${rows.length - scoredRows.length} unscored`}
                  icon={Shield}
                  bg="bg-indigo-50" fg="text-indigo-600" iconColor="#6366f1"
                />
                <StatCard
                  title="High Residual"
                  value={insights.highBand}
                  subtext={`Residual 15+ · ${insights.extreme} extreme, ${insights.high} high`}
                  trendIcon={TrendingUp}
                  bg="bg-red-50" fg="text-red-500" iconColor="#ef4444"
                />
                <StatCard
                  title="Medium Residual"
                  value={insights.mediumBand}
                  subtext="Residual 8–14 (moderate)"
                  trendIcon={Minus}
                  bg="bg-amber-50" fg="text-amber-500" iconColor="#f59e0b"
                />
                <StatCard
                  title="Low Residual"
                  value={insights.lowBand}
                  subtext={`Residual < 8 · ${insights.low} low, ${insights.vlow} v.low`}
                  trendIcon={TrendingDown}
                  bg="bg-emerald-50" fg="text-emerald-600" iconColor="#10b981"
                />
              </div>

              {/* Matrices + Distributions */}
              <div className="flex gap-4 items-stretch">

                {/* Left: legend + two matrices */}
                <div className="flex flex-col gap-3 flex-1 min-w-0">
                  {/* Shared legend row */}
                  <div className="flex items-center justify-between px-1">
                    <span className="text-[11px] text-slate-400 font-medium flex items-center gap-1.5">
                      <Activity size={12} className="text-indigo-400" />
                      Strategic view of organizational risk exposure, movement, and concentration.
                    </span>
                    <div className="flex gap-3 text-[11px] font-medium text-slate-500">
                      {LEGEND.map(l => (
                        <div key={l.label} className="flex items-center gap-1">
                          <div className={`w-2.5 h-2.5 rounded-sm ${l.cls}`} />
                          {l.label}
                        </div>
                      ))}
                    </div>
                  </div>
                  {/* Two matrices side by side — flex-1 so they fill remaining height */}
                  <div className="grid grid-cols-2 gap-3 flex-1">
                    <RiskMatrix
                      title="Inherent Risk Assessment"
                      description={`Raw risk exposure (L × S) for ${scoredRows.length} scored risks.`}
                      icon={AlertTriangle}
                      risks={scoredRows}
                      getCell={(r: RiskRow) => r.likelihood}
                      onCellClick={handleCellClick("inherent")}
                    />
                    <RiskMatrix
                      title="Residual Risk Assessment"
                      description="Position after controls applied (likelihood derived from residual / severity)."
                      icon={Shield}
                      risks={scoredRows}
                      getCell={(r: RiskRow) => r.residualLikelihood}
                      onCellClick={handleCellClick("residual")}
                    />
                  </div>
                </div>

                {/* Right: distribution panels — stretch to match left column height */}
                <div className="flex flex-col gap-3 shrink-0" style={{ width: 'calc(25% - 0.375rem)' }}>
                  <DistributionWidget
                    title="Residual Risk Distribution"
                    data={insights.residualBands}
                    icon={Shield}
                    total={scoredRows.length}
                    accentColor="#ef4444"
                  />
                  <DistributionWidget
                    title="Risk by Category"
                    data={insights.categoryCounts}
                    icon={Briefcase}
                    total={scoredRows.length}
                    accentColor="#6366f1"
                  />
                  <DistributionWidget
                    title="Severity Distribution"
                    data={[1, 2, 3, 4, 5].map(s => ({
                      label: `Severity ${s}`,
                      count: scoredRows.filter(r => r.severity === s).length,
                    }))}
                    icon={Globe}
                    total={scoredRows.length}
                    accentColor="#3b82f6"
                    className="flex-1"
                  />
                </div>

              </div>

            </div>
          )}
        </div>
      </div>

      <RiskDetailModal
        isOpen={modalState.isOpen}
        onClose={() => setModalState(prev => ({ ...prev, isOpen: false }))}
        data={modalState.risks}
        context={modalState.context}
        metric={modalState.metric}
        onVisualize={handleVisualize}
      />

      <Modal
        isOpen={visModalState.isOpen}
        onClose={() => setVisModalState({ isOpen: false, risk: null })}
        size="lg"
        closeOnOverlayClick={false}
      >
        <Modal.Header>Risk Canvas</Modal.Header>
        <Modal.Body className="!p-0 overflow-hidden">
          {visModalState.risk && (
            <RiskVisualization
              risk={visModalState.risk}
              onClose={() => setVisModalState({ isOpen: false, risk: null })}
            />
          )}
        </Modal.Body>
      </Modal>

      <Toast toast={toast} onClose={() => setToast(null)} />
    </div>
  );
};

export default RiskOverview;
